import React, { useEffect, useState, useMemo } from 'react';
import { Box, IconButton, Popover, Typography, styled, Grid2, FormControlLabel, Checkbox, Chip, TextField, InputBase } from '@mui/material';
import { useTranslation } from 'react-i18next';
import DragListView from 'react-drag-listview';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { PrimaryFormActionButton } from '../../../../../Components/StyledComponent';
import CustomIcon from '../../../../../Components/CustomIcon';
import SignalsSelectionSearch from '../SignalsSelectionSearch';

const MAX_METRICS = 10;

const popoverPaperStyles = {
    p: '16px',
    borderRadius: '10px',
    backgroundColor: '#FFFFFF',
    boxShadow: '2px 2px 4px 0px rgba(151, 151, 151, 0.25), -2px -2px 4px 0px rgba(151, 151, 151, 0.25)'
}

const VitalChip = styled(Chip)(({ theme, open }) => ({
    background: "#FFFFFF",
    border: open ? "1px solid #8F93FF" : "1px solid #E6E6E6",
    borderRadius: "8px",
    fontSize: "12px",
    fontWeight: 500,
    color: open ? '#6D72F6' : "#686868",
    display: "flex",
    gap: '4px',
    height: '22px',
    alignItems: "center",
    whiteSpace: "nowrap",
    overflow: "hidden",

    "&:hover": {
        backgroundColor: "#EAEAEA",
    },

    "& .MuiChip-label": {
        paddingLeft: '8px',
        paddingRight: '4px',
    },
    "& .MuiChip-deleteIcon": {
        color: open ? "#6D72F6" : "#686868",
        fontSize: '12px',
        borderRadius: "50%",
        marginRight: '5px',
        padding: '3px',
        "&:hover": {
            backgroundColor: "#E6E6E6",
            color: "#970625",
        },
    },
}))

const HeaderBox = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
});

const FooterBox = styled(Box)({
    padding: 0,
    display: 'flex',
    justifyContent: 'flex-end',
});

const ItemRowCheckBoxLabel = styled(FormControlLabel)(({ theme }) => ({
    display: 'block',
    marginLeft: 0,

    '.MuiButtonBase-root': {
        padding: 0
    },

    '.MuiCheckbox-root': {
        padding: 0,
        color: '#A2A2A2 !important',

        '&.Mui-checked': {
            color: '#6D72F6 !important',
        },
        '&.Mui-disabled': {
            color: '#A2A2A2 !important',
        }
    },

    '.MuiSvgIcon-root': {
        width: '20px',
        height: '20px'
    }
}));

const ItemRow = ({ item, initialItems, onClick }) => {
    const isSelected = useMemo(
        () => initialItems.some((m) => m.label === item.label),
        [initialItems, item.label]
    );
    const isFull = initialItems.length >= MAX_METRICS;

    const canModify = isSelected || !isFull;


    return (
        <Grid2 container gap={2} alignItems="center" sx={{ py: 0.5 }}>
            <CustomIcon name='draggable-icon' cursor='grab' containerProps={{ className: "draggable-icon" }} />
            <ItemRowCheckBoxLabel
                key={item.id}
                disabled={!canModify}
                control={<Checkbox
                    sx={{ color: '#6D72F6', width: '20px', height: '20px' }}
                    checked={isSelected}
                    onChange={() => onClick(item)}
                />}
                label={<Typography variant={canModify ? 'fw400s12lh24c3D4B5A' : 'fw400s12lh24c989898'}> {item.label} </Typography>}
            />
        </Grid2>
    );
};

export default function BinarySignalsSelection({
    itemList = [],
    selectedItems,
    applySelectedItems,
    handleSelectionOnClick,
}) {
    const { t } = useTranslation(['common', 'generic_table']);
    const [anchorEl, setAnchorEl] = useState(null);
    const [initialItems, setInitialItems] = useState(selectedItems);
    const [draggableItemList, setDraggableItemList] = useState(itemList);
    const [searchTerm, setSearchTerm] = useState('');

    const open = Boolean(anchorEl);
    const id = open ? 'digital-twin-comparison-matrix-popover' : undefined;

    // Filter items whenever the search term changes
    const filteredItems = useMemo(() => {
        if (!searchTerm.trim()) return draggableItemList;
        return draggableItemList.filter(item =>
            item.label.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [searchTerm, draggableItemList]);

    const handleClick = (event) => {
        const target = event.currentTarget;

        // First, just check if scroll is needed
        const needsScroll = handleSelectionOnClick(true); // pass flag to only check

        if (needsScroll) {
            // Full scroll logic
            handleSelectionOnClick();
            setTimeout(() => setAnchorEl(target), 500);
        } else {
            // Open popup instantly
            setAnchorEl(target);

            // Run rest of handleSelectionOnClick after popup is already opening
            requestAnimationFrame(() => {
                handleSelectionOnClick();
            });
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleRowClick = (metric) => {
        let updated = [...initialItems];
        const isSelected = initialItems.some((m) => m.label === metric.label);

        if (isSelected) {
            updated = updated.filter((m) => m.label !== metric.label);
        } else {
            if (updated.length >= MAX_METRICS) {
                updated.shift();
            }
            updated.push({ ...metric, selected: true });
        }

        setInitialItems(updated);
    };

    const removeSelectedMetric = (selectedItem) => {
        let updated = [...initialItems];
        updated = updated.filter((m) => m.label !== selectedItem.label);
        applySelectedItems(updated);
    }

    const dragProps = {
        onDragEnd(fromIndex, toIndex) {
            const updatedList = [...draggableItemList];
            const item = updatedList.splice(fromIndex, 1)[0];
            updatedList.splice(toIndex, 0, item);
            setDraggableItemList(updatedList);
            const updatedInitialItems = [];
            updatedList.forEach((item) => {
                const selectedItem = initialItems.find((m) => m.label === item.label);
                if (selectedItem) {
                    updatedInitialItems.push(selectedItem);
                }
            });
            setInitialItems(updatedInitialItems);
        },
        nodeSelector: "div[data-drag-row]",
        handleSelector: ".draggable-icon" // or anything else you want as a drag handle
    };


    const renderSelectedItems = () => {
        if ((selectedItems ?? []).length === 0) return null;

        const visibleItems = selectedItems.slice(0, 4);
        const remainingCount = selectedItems.length - 4;

        return (
            <>
                {visibleItems.map((selectedItem, index) => (
                    <VitalChip
                        open={open}
                        onClick={handleClick}
                        key={index}
                        label={selectedItem.label}
                        onDelete={() => removeSelectedMetric(selectedItem)}
                        deleteIcon={<CloseRoundedIcon />}
                    />
                ))}
                {remainingCount > 0 && (
                    <VitalChip
                        open={open}
                        onClick={handleClick}
                        label={`+${remainingCount}`}
                    />
                )}
            </>
        );
    };

    const sortItemListInSelectedOrder = () => {
        const selectedKeyOrder = new Map(
            (selectedItems ?? []).map((item, index) => [item.key, index])
        );

        return [...itemList].sort((a, b) => {
            const aIndex = selectedKeyOrder.has(a.key) ? selectedKeyOrder.get(a.key) : Infinity;
            const bIndex = selectedKeyOrder.has(b.key) ? selectedKeyOrder.get(b.key) : Infinity;
            return aIndex - bIndex;
        });
    }

    useEffect(() => {
        setInitialItems(selectedItems);
    }, [selectedItems, open]);

    useEffect(() => {
        setDraggableItemList(sortItemListInSelectedOrder()); // sync on load
    }, [itemList, open]);


    return (
        <Box>
            <Grid2 container gap={'4px'} sx={{
                border: open ? '1px solid #D4D5FC' : '1px solid #E6E6E6',
                borderRadius: '10px',
                p: 0.5,
            }}>
                {renderSelectedItems()}

                <IconButton
                    sx={{
                        p: 0,
                        borderRadius: '6px',
                        padding: '0px',
                        'svg path': { fill: open ? '#6D72F6' : '' },
                    }}
                    onClick={handleClick}
                >
                    <CustomIcon name="primary-arrow-icon" rotate={open ? -90 : 90} />
                </IconButton>
            </Grid2>

            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{ paper: { sx: popoverPaperStyles } }}
            >
                <HeaderBox>
                    <Typography variant="fw700s14c3D4B5A">
                        {t("Select Categorical Signals", "Select Categorical Signals")}
                    </Typography>
                </HeaderBox>

                <Box sx={{ my: 1.5, minWidth: '275px' }}>
                    <SignalsSelectionSearch value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />

                    <Typography variant="fw500s10lh24c5E758D" sx={{ lineHeight: '14px', color: "#8D8D8D", mt: 1, display: 'block' }}>
                        {t('METRIC')}
                    </Typography>

                    <Box sx={{ maxHeight: '225px', overflowY: 'auto' }}>
                        <DragListView {...dragProps}>
                            <div>
                                {filteredItems.map((item, index) => (
                                    <div key={index} data-drag-row>
                                        <ItemRow
                                            item={item}
                                            initialItems={initialItems}
                                            onClick={handleRowClick}
                                        />
                                    </div>
                                ))}
                            </div>
                        </DragListView>
                    </Box>
                </Box>

                <FooterBox>
                    <PrimaryFormActionButton
                        onClick={() => {
                            applySelectedItems(initialItems);
                            handleClose();
                        }}>
                        {t('actions.apply')}
                    </PrimaryFormActionButton>
                </FooterBox>
            </Popover>
        </Box>
    );
}
